import type { AccountMonth, SeriesResult, StockQuery, YearMonth } from '../data/types';
import { assertMovingAverage, inSet, trailingAverage } from './shared';

function breakdownId(row: AccountMonth, query: StockQuery): string {
  switch (query.breakdown) {
    case 'none':
      return 'total';
    case 'account':
      return row.account_id;
    case 'group':
      return row.account_group_id;
  }
}

export function queryStock(
  accountMonths: readonly AccountMonth[],
  months: readonly YearMonth[],
  query: StockQuery,
): SeriesResult {
  assertMovingAverage(query.movingAverage);
  if (query.visiblePeriod.from > query.visiblePeriod.to) {
    throw new RangeError('Il periodo visibile è invertito.');
  }

  const visibleMonths = months.filter(
    (month) => month >= query.visiblePeriod.from && month <= query.visiblePeriod.to,
  );
  if (visibleMonths.length === 0) {
    return { buckets: [], series: [], total: 0, unit: 'cents' };
  }

  const firstVisible = months.indexOf(visibleMonths[0] ?? query.visiblePeriod.from);
  const lookback = (query.movingAverage?.window ?? 1) - 1;
  const extendedMonths = months.slice(
    Math.max(0, firstVisible - lookback),
    firstVisible + visibleMonths.length,
  );
  const monthIndex = new Map(extendedMonths.map((month, index) => [month, index]));
  const valuesBySeries = new Map<string, number[]>();

  for (const row of accountMonths) {
    if (!inSet(row.account_id, query.accountIds)) continue;
    if (!inSet(row.account_group_id, query.accountGroupIds)) continue;
    const index = monthIndex.get(row.month);
    if (index === undefined) continue;
    const id = breakdownId(row, query);
    const values = valuesBySeries.get(id) ?? Array<number>(extendedMonths.length).fill(0);
    const value = query.metric === 'closing-balance' ? row.closing_balance_cents : row.delta_cents;
    values[index] = (values[index] ?? 0) + value;
    valuesBySeries.set(id, values);
  }

  if (query.breakdown === 'none' && !valuesBySeries.has('total')) {
    valuesBySeries.set('total', Array<number>(extendedMonths.length).fill(0));
  }

  const visibleStart = extendedMonths.indexOf(visibleMonths[0] ?? query.visiblePeriod.from);
  const series = Array.from(valuesBySeries, ([id, values]) => {
    const calculated = query.movingAverage
      ? trailingAverage(values, query.movingAverage.window)
      : values;
    return { id, label: id, values: calculated.slice(visibleStart) };
  }).sort((left, right) => left.id.localeCompare(right.id));

  const total =
    query.metric === 'closing-balance'
      ? series.reduce((sum, item) => sum + (item.values.at(-1) ?? 0), 0)
      : series.reduce(
          (sum, item) => sum + item.values.reduce((seriesSum, value) => seriesSum + value, 0),
          0,
        );

  return { buckets: visibleMonths, series, total, unit: 'cents' };
}
